// src/components/admin/CreateContractAdmin.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminShell } from './Sidebar';
import { LoadingState } from '../ui/CrmPage';

export default function CreateContractAdmin() {
  const navigate = useNavigate();
  const apiBase = useMemo(() => {
    const base = process.env.REACT_APP_API_URL || '';
    return base.endsWith('/') ? base : `${base}/`;
  }, []);

  const [clients, setClients] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loadingRefs, setLoadingRefs] = useState(true);
  const [refError, setRefError] = useState('');

  const [form, setForm] = useState({
    contract_number: '', contract_date: '', electronic_version: '',
    remaining_amount: '', id_client: '', id_organization: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [ok, setOk] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingRefs(true); setRefError('');
      try {
        const [rClients, rOrgs] = await Promise.all([
          fetch(`${apiBase}v1/clients`),
          fetch(`${apiBase}v1/organizations`),
        ]);
        if (!rClients.ok) throw new Error(`Клиенты: HTTP ${rClients.status}`);
        if (!rOrgs.ok) throw new Error(`Организации: HTTP ${rOrgs.status}`);
        const c = await rClients.json();
        const o = await rOrgs.json();
        if (!cancelled) {
          setClients(Array.isArray(c) ? c : []);
          setOrgs(Array.isArray(o) ? o : []);
        }
      } catch (e) {
        if (!cancelled) setRefError(e.message || 'Ошибка загрузки справочников');
      } finally {
        if (!cancelled) setLoadingRefs(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiBase]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!form.contract_number.trim()) errs.contract_number = 'Укажите номер договора';
    if (!form.contract_date) errs.contract_date = 'Укажите дату договора';
    if (!form.electronic_version.trim()) errs.electronic_version = 'Укажите ссылку/идентификатор';
    const amountStr = String(form.remaining_amount).replace(',', '.');
    const amount = Number(amountStr);
    if (amountStr === '' || Number.isNaN(amount)) errs.remaining_amount = 'Введите сумму';
    else if (amount < 0) errs.remaining_amount = 'Сумма не может быть отрицательной';
    else if (amount > 99999999.99) errs.remaining_amount = 'Слишком большая сумма';
    if (!form.id_client) errs.id_client = 'Выберите клиента';
    if (!form.id_organization) errs.id_organization = 'Выберите организацию';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(''); setOk('');
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        contract_number: form.contract_number.trim(),
        contract_date: form.contract_date,
        electronic_version: form.electronic_version.trim(),
        remaining_amount: Number(String(form.remaining_amount).replace(',', '.')),
        id_client: Number(form.id_client),
        id_organization: Number(form.id_organization),
      };
      const r = await fetch(`${apiBase}v1/contracts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const t = await r.text().catch(() => '');
        throw new Error(`Ошибка сохранения: ${r.status} ${t}`.trim());
      }
      setOk('Договор успешно создан');
      setForm({
        contract_number: '', contract_date: '', electronic_version: '',
        remaining_amount: '', id_client: '', id_organization: '',
      });
    } catch (e) {
      setSubmitError(e.message || 'Не удалось создать договор');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdminShell
      breadcrumbs="Клиенты и договоры"
      title="Создать договор"
      subtitle="Внесение нового договора в систему"
      actions={
        <>
          <button type="button" className="adminbtn" onClick={() => navigate(-1)}>Отмена</button>
          <button
            type="submit"
            form="create-contract-form"
            className="adminbtn adminbtn--primary"
            disabled={submitting || loadingRefs}
          >
            {submitting ? 'Сохраняем…' : 'Сохранить договор'}
          </button>
        </>
      }
    >
      <div className="crmpage crmpage--embedded">
        <div className="crmpage__main">
          {!!refError && <div className="crmpage__alert crmpage__alert--error">{refError}</div>}
          {!!submitError && <div className="crmpage__alert crmpage__alert--error">{submitError}</div>}
          {!!ok && <div className="crmpage__alert" style={{ background: 'var(--success-bg)', borderColor: 'var(--success-border)', color: '#0F5B3C' }}>{ok}</div>}

          {loadingRefs ? (
            <LoadingState text="Загружаем справочники…" />
          ) : (
            <form id="create-contract-form" className="adminform" onSubmit={onSubmit} style={{ maxWidth: 880 }}>
              <section className="adminform__card">
                <div className="adminform__section-title">Реквизиты договора</div>
                <div className="adminform__grid">
                  <label className="adminform__field">
                    <span className="adminform__label">Номер договора<span className="req">*</span></span>
                    <input className="adminform__input" name="contract_number" value={form.contract_number} onChange={onChange} required />
                    {errors.contract_number && <span className="adminform__error">{errors.contract_number}</span>}
                  </label>
                  <label className="adminform__field">
                    <span className="adminform__label">Дата договора<span className="req">*</span></span>
                    <input type="date" className="adminform__input" name="contract_date" value={form.contract_date} onChange={onChange} required />
                    {errors.contract_date && <span className="adminform__error">{errors.contract_date}</span>}
                  </label>
                  <label className="adminform__field">
                    <span className="adminform__label">Электронная версия<span className="req">*</span></span>
                    <input className="adminform__input" name="electronic_version" value={form.electronic_version} onChange={onChange} placeholder="URL или идентификатор" required />
                    {errors.electronic_version && <span className="adminform__error">{errors.electronic_version}</span>}
                  </label>
                  <label className="adminform__field">
                    <span className="adminform__label">Остаток, ₸<span className="req">*</span></span>
                    <input className="adminform__input" name="remaining_amount" value={form.remaining_amount} onChange={onChange} inputMode="decimal" placeholder="12345.67" required />
                    {errors.remaining_amount && <span className="adminform__error">{errors.remaining_amount}</span>}
                  </label>
                  <label className="adminform__field">
                    <span className="adminform__label">Клиент<span className="req">*</span></span>
                    <select className="adminform__select" name="id_client" value={form.id_client} onChange={onChange} required>
                      <option value="">— выберите —</option>
                      {clients.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name || `Клиент #${c.id}`}{c.bin ? ` — ${c.bin}` : ''}
                        </option>
                      ))}
                    </select>
                    {errors.id_client && <span className="adminform__error">{errors.id_client}</span>}
                  </label>
                  <label className="adminform__field">
                    <span className="adminform__label">Организация<span className="req">*</span></span>
                    <select className="adminform__select" name="id_organization" value={form.id_organization} onChange={onChange} required>
                      <option value="">— выберите —</option>
                      {orgs.map((o) => <option key={o.id} value={o.id}>{o.name || `#${o.id}`}</option>)}
                    </select>
                    {errors.id_organization && <span className="adminform__error">{errors.id_organization}</span>}
                  </label>
                </div>
              </section>
            </form>
          )}
        </div>
      </div>
    </AdminShell>
  );
}
